<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import { listAlertOptions, type Alert } from '../api/alerts'
import { listDemandOptions, type Demand } from '../api/demands'
import { toRFC3339 } from '../api/datetime'
import {
  ACTION_LABELS,
  ACTION_TYPES,
  BOARD_COLUMNS,
  STATUS_LABELS,
  STATUS_TAG,
  cancelResponse,
  createResponse,
  doneResponse,
  listResponses,
  responseInput,
  type ResponseActionType,
  type ResponseForm,
  type ResponseLink,
  type ResponseRow,
  type ResponseStatus,
} from '../api/responses'

/** 看板一次拉全量再按状态分列：列头要显示每列的真实数量，服务端分组得后端另开接口 */
const all = ref<ResponseRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const refError = ref<unknown>(null)
const notice = ref('')
/** 参照（预警 / 需求的单号表）：卡片要显示关联对象单号，响应列表只给数字 id */
const alerts = ref<Alert[]>([])
const demands = ref<Demand[]>([])

const query = reactive({ keyword: '', status: '', action: '' })

const byStatus = computed<Record<ResponseStatus, ResponseRow[]>>(() => {
  const out: Record<ResponseStatus, ResponseRow[]> = { pending: [], done: [], cancelled: [] }
  for (const row of all.value) {
    if (out[row.status as ResponseStatus]) out[row.status as ResponseStatus].push(row)
  }
  return out
})

/** 新建响应：关联预警 / 关联需求 二选一（后端 alert_id 与 demand_id 异或校验） */
const formOpen = ref(false)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<ResponseForm>({
  link: 'alert',
  alert_id: undefined,
  demand_id: undefined,
  action: '',
  owner: '',
  due_at: null,
  detail: '',
})

/** 完成 / 取消的说明（后端追加到 detail 末尾，可留空） */
const actOpen = ref(false)
const actTarget = ref<ResponseRow | null>(null)
const actKind = ref<'done' | 'cancel'>('done')
const actSaving = ref(false)
const actError = ref<unknown>(null)
const actForm = reactive({ detail: '' })

const linkError = computed(() => {
  if (form.link === 'alert' && !form.alert_id) return '请选择关联的预警'
  if (form.link === 'demand' && !form.demand_id) return '请选择关联的需求'
  return ''
})

const actionError = computed(() => (form.action ? '' : '请选择响应动作'))

const dueError = computed(() => (toRFC3339(form.due_at) ? '' : '请选择预计完成时间（后端必填）'))

const ownerError = computed(() => (form.owner.trim() ? '' : '请填写负责人'))

const formErrorText = computed(
  () => linkError.value || actionError.value || dueError.value || ownerError.value,
)

const alertDoc = computed(() => new Map(alerts.value.map((a) => [a.id, a.doc_no])))
const demandDoc = computed(() => new Map(demands.value.map((d) => [d.id, d.doc_no])))

/** 关联对象只给数字 id，单号要靠参照表补；参照没拿到时退化成 #id 而不是空白 */
function linkLabel(row: ResponseRow): string {
  if (row.alert_id) return alertDoc.value.get(row.alert_id) ?? `#${row.alert_id}`
  if (row.demand_id) return demandDoc.value.get(row.demand_id) ?? `#${row.demand_id}`
  return '—'
}

function linkKind(row: ResponseRow): string {
  return row.alert_id ? '预警' : '需求'
}

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listResponses({
      page: 1,
      size: 200,
      keyword: query.keyword,
      status: query.status,
      action: query.action,
    })
    all.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    all.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    const [a, d] = await Promise.all([listAlertOptions(), listDemandOptions()])
    alerts.value = a
    demands.value = d
  } catch (e) {
    // 参照数据拿不到，卡片只能显示 #id，但必须把原因露出来，否则看着像没关联对象
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  void load()
}

function openCreate(): void {
  formError.value = null
  Object.assign(form, {
    link: 'alert' as ResponseLink,
    alert_id: undefined,
    demand_id: undefined,
    action: '',
    owner: '',
    due_at: null,
    detail: '',
  })
  formOpen.value = true
}

/** 切换关联方式时清掉另一个 id，避免提交时两边都带上 */
function onLinkChange(kind: ResponseLink): void {
  form.link = kind
  form.alert_id = undefined
  form.demand_id = undefined
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  try {
    // responseInput 只送被选中的那个 id：后端 hasAlert == hasDemand 即 422
    const created = await createResponse(responseInput(form))
    formOpen.value = false
    await load(`响应 ${created.doc_no} 已创建`)
  } catch (e) {
    // 两个 id 都空 / 都填、动作非法、负责人或截止时间为空都是后端 422，trace_id 原样露出
    formError.value = e
  } finally {
    saving.value = false
  }
}

function openAct(row: ResponseRow, kind: 'done' | 'cancel'): void {
  actError.value = null
  actTarget.value = row
  actKind.value = kind
  Object.assign(actForm, { detail: '' })
  actOpen.value = true
}

async function submitAct(): Promise<void> {
  if (!actTarget.value) return
  actSaving.value = true
  actError.value = null
  try {
    const detailText = actForm.detail.trim()
    const input = detailText ? { detail: detailText } : {}
    const saved =
      actKind.value === 'done'
        ? await doneResponse(actTarget.value.id, input)
        : await cancelResponse(actTarget.value.id, input)
    actOpen.value = false
    await load(`响应 ${saved.doc_no} 已${actKind.value === 'done' ? '完成' : '取消'}`)
  } catch (e) {
    // 非 pending 状态后端 409，把 trace_id 露出来
    actError.value = e
  } finally {
    actSaving.value = false
  }
}
</script>

<template>
  <div class="yc-resp">
    <header class="yc-resp__bar">
      <el-input
        v-model="query.keyword"
        class="yc-resp__keyword"
        placeholder="响应单号 / 动作"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.status"
        class="yc-resp__ref"
        placeholder="全部状态"
        clearable
        @change="search"
      >
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select
        v-model="query.action"
        class="yc-resp__ref"
        placeholder="全部动作"
        clearable
        @change="search"
      >
        <el-option v-for="a in ACTION_TYPES" :key="a" :label="ACTION_LABELS[a]" :value="a" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'scr:response:create'" type="primary" @click="openCreate">新建响应</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-resp__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-resp__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <p v-if="total > 0 && all.length < total" class="yc-resp__hint">
      看板一次最多展示 200 条，当前命中 {{ total }} 条，已显示 {{ all.length }} 条 —— 请用筛选缩小范围。
    </p>

    <el-row v-loading="loading" :gutter="16" class="yc-resp__board">
      <el-col v-for="status in BOARD_COLUMNS" :key="status" :xs="24" :md="8">
        <el-card class="yc-resp__col" shadow="never">
          <template #header>
            <div class="yc-resp__colhead">
              <el-tag :type="STATUS_TAG[status]" size="small">{{ STATUS_LABELS[status] }}</el-tag>
              <span class="yc-resp__count">{{ byStatus[status].length }} 条</span>
            </div>
          </template>

          <div v-if="byStatus[status].length" class="yc-resp__cards">
            <div v-for="row in byStatus[status]" :key="row.id" class="yc-resp__item">
              <div class="yc-resp__itemhead">
                <span class="yc-resp__doc">{{ row.doc_no }}</span>
                <el-tag size="small" type="info">
                  {{ ACTION_LABELS[row.action as ResponseActionType] ?? row.action }}
                </el-tag>
              </div>
              <div class="yc-resp__line">
                <span class="yc-resp__key">关联{{ linkKind(row) }}</span>
                <span class="yc-resp__val">{{ linkLabel(row) }}</span>
              </div>
              <div class="yc-resp__line">
                <span class="yc-resp__key">负责人</span>
                <span class="yc-resp__val">{{ row.owner || '—' }}</span>
              </div>
              <div class="yc-resp__line">
                <span class="yc-resp__key">截止</span>
                <span class="yc-resp__val">{{ formatDateTime(row.due_at) }}</span>
              </div>
              <p v-if="row.detail" class="yc-resp__detail">{{ row.detail }}</p>
              <div v-if="row.done_at" class="yc-resp__line">
                <span class="yc-resp__key">{{ row.status === 'done' ? '完成于' : '取消于' }}</span>
                <span class="yc-resp__val">{{ formatDateTime(row.done_at) }}</span>
              </div>
              <div v-if="status === 'pending'" class="yc-resp__ops">
                <el-button
                  v-perm="'scr:response:done'"
                  link
                  type="success"
                  @click="openAct(row, 'done')"
                >
                  完成
                </el-button>
                <el-button
                  v-perm="'scr:response:cancel'"
                  link
                  type="danger"
                  @click="openAct(row, 'cancel')"
                >
                  取消
                </el-button>
              </div>
            </div>
          </div>

          <EmptyState v-else-if="!loading" title="暂无" description="这一列还没有响应动作。" />
        </el-card>
      </el-col>
    </el-row>

    <!-- 新建响应：关联预警 / 关联需求 二选一 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      title="新建响应"
      width="600px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-resp__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="关联方式">
          <el-radio-group :model-value="form.link" @update:model-value="onLinkChange">
            <el-radio value="alert">关联预警</el-radio>
            <el-radio value="demand">关联需求</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.link === 'alert'" label="预警" :error="linkError">
          <el-select v-model="form.alert_id" filterable class="yc-resp__full" placeholder="选择预警">
            <el-option
              v-for="a in alerts"
              :key="a.id"
              :label="`${a.doc_no} ${a.title}`.trim()"
              :value="a.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item v-else label="需求" :error="linkError">
          <el-select v-model="form.demand_id" filterable class="yc-resp__full" placeholder="选择需求">
            <el-option
              v-for="d in demands"
              :key="d.id"
              :label="`${d.doc_no} ${d.item_name}`.trim()"
              :value="d.id"
            />
          </el-select>
        </el-form-item>

        <div class="yc-resp__row2">
          <el-form-item label="动作" :error="actionError">
            <el-select v-model="form.action" class="yc-resp__full" placeholder="选择动作">
              <el-option
                v-for="a in ACTION_TYPES"
                :key="a"
                :label="ACTION_LABELS[a]"
                :value="a"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="负责人" :error="ownerError">
            <el-input v-model="form.owner" maxlength="64" placeholder="如 张伟" />
          </el-form-item>
        </div>

        <el-form-item label="预计完成时间" :error="dueError">
          <el-date-picker
            v-model="form.due_at"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            placeholder="后端必填"
          />
        </el-form-item>

        <el-form-item label="说明">
          <el-input v-model="form.detail" maxlength="1000" placeholder="可留空" />
        </el-form-item>

        <p class="yc-resp__hint">
          预警与需求只能关联一个：两个都空或都填后端都会挡成 422。
          完成响应时，该预警下若已无待执行响应，后端会把预警自动置为已解决。
        </p>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          创建
        </el-button>
        <span v-if="formErrorText" class="yc-resp__hint yc-resp__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 完成 / 取消：说明追加到响应详情末尾 -->
    <el-dialog
      v-if="actOpen"
      v-model="actOpen"
      :title="`${actKind === 'done' ? '完成' : '取消'}响应 · ${actTarget?.doc_no ?? ''}`"
      width="460px"
      :close-on-click-modal="false"
    >
      <div v-if="actError" class="yc-resp__error">
        <TraceIdAlert :error="actError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitAct">
        <el-form-item label="说明">
          <el-input
            v-model="actForm.detail"
            type="textarea"
            :rows="3"
            maxlength="1000"
            :placeholder="actKind === 'done' ? '可留空，填写后以「[完成] …」追加' : '可留空，填写后以「[取消] …」追加'"
          />
        </el-form-item>
        <el-button type="primary" native-type="submit" :loading="actSaving">确认</el-button>
      </el-form>

      <template #footer>
        <el-button @click="actOpen = false">返回</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-resp {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__ref {
    width: 130px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    margin: 0 0 var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__board {
    align-items: stretch;
  }

  &__col {
    height: 100%;
  }

  &__colhead {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__count {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__cards {
    display: flex;
    flex-direction: column;
    gap: var(--yc-space-3);
  }

  &__item {
    padding: var(--yc-space-3);
    border: 1px solid var(--yc-border);
    border-radius: var(--yc-radius, 6px);
  }

  &__itemhead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-2);
  }

  &__doc {
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__line {
    display: flex;
    gap: var(--yc-space-2);
    font-size: var(--yc-font-sm);
    line-height: 1.9;
  }

  &__key {
    min-width: 72px;
    color: var(--yc-text-secondary);
  }

  &__val {
    word-break: break-all;
  }

  &__detail {
    margin: var(--yc-space-2) 0 0;
    padding: var(--yc-space-2);
    border-radius: var(--yc-radius, 6px);
    background: var(--yc-bg-subtle, #f7f8fa);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;
    white-space: pre-wrap;
  }

  &__ops {
    display: flex;
    gap: var(--yc-space-3);
    margin-top: var(--yc-space-2);
  }

  &__row2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-3);
  }
}
</style>
